<script lang="ts">
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { marketingCanonicalPath, type NavBarAppearance } from '$lib/utils/marketing-routes';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import { currentUser } from '@/utils/ApiClient';
	import ChevronDown from '@lucide/svelte/icons/chevron-down';
	import MarketingAccountMenu from './MarketingAccountMenu.svelte';
	import MarketingLocalePicker from './MarketingLocalePicker.svelte';

	interface Props {
		appearance?: NavBarAppearance;
		hideOnMobile?: boolean;
	}

	let { appearance = 'compact', hideOnMobile = false }: Props = $props();

	const locale = $derived(getLocale());

	const canonicalPath = $derived(marketingCanonicalPath(page.url.pathname));

	const isCapitanPage = $derived(canonicalPath === '/registro-capitan');
	const isAddBoatPage = $derived(canonicalPath === '/anadir-barco');
	const isLoginPage = $derived(canonicalPath === '/iniciar-sesion');
	const showLoginLink = $derived(!isAddBoatPage);

	const ctaHref = $derived.by(() => {
		void locale;
		return isCapitanPage
			? '#registro'
			: isAddBoatPage
				? $currentUser
					? localizeHref('/cuenta')
					: localizeHref('/iniciar-sesion')
				: localizeHref('/anadir-barco');
	});

	const ctaLabel = $derived.by(() => {
		void locale;
		return isCapitanPage
			? m.nav_cta_register_skipper()
			: isAddBoatPage
				? $currentUser
					? m.nav_cta_my_account()
					: m.new_upper_thrush_flow()
				: m.landing_cta_register();
	});

	const featured = $derived.by(() => {
		void locale;
		return {
			href: localizeHref('/alquiler-lista-septima'),
			tag: m.nav_dd_tag(),
			label: m.nav_dd_list7_label(),
			desc: m.nav_dd_list7_desc(),
			cta: m.landing_mod7_link(),
			match: '/alquiler-lista-septima'
		};
	});

	const howItWorks = $derived.by(() => {
		void locale;
		return [
			{
				href: localizeHref('/zarpar-experiencias'),
				label: m.nav_dd_exp_label(),
				desc: m.nav_dd_exp_desc(),
				match: '/zarpar-experiencias'
			},
			{
				href: localizeHref('/alquiler-lista-sexta'),
				label: m.nav_dd_charter_label(),
				desc: m.nav_dd_charter_desc(),
				match: '/alquiler-lista-sexta'
			}
		] as const;
	});

	let open = $state(false);
	let closeTimer: ReturnType<typeof setTimeout> | undefined;

	const howIsActive = $derived(
		featured.match === canonicalPath || howItWorks.some((item) => item.match === canonicalPath)
	);

	function openMenu() {
		clearTimeout(closeTimer);
		open = true;
	}

	function scheduleClose() {
		clearTimeout(closeTimer);
		closeTimer = setTimeout(() => (open = false), 120);
	}

	const isCompact = $derived(appearance === 'compact');

	const navShellClass = $derived.by(() => {
		const parts: string[] = [];
		if (hideOnMobile) parts.push('hidden md:block');
		if (appearance === 'overlay') {
			parts.push('absolute top-0 right-0 left-0 z-20');
		} else {
			parts.push('sticky top-0 z-30 border-b border-zp-line bg-zp-paper/86 backdrop-blur-[10px]');
			if (appearance === 'legal' || appearance === 'compact') {
				parts.push('md:bg-white md:backdrop-blur-none');
			}
		}
		return parts.join(' ');
	});

	const innerShellClass = $derived(
		isCompact
			? 'flex w-full items-center justify-between gap-3 py-[18px] pl-4 pr-4 max-[400px]:gap-4 max-[400px]:py-3 md:gap-2 md:py-2 md:pr-6 lg:pr-8'
			: 'mx-auto flex max-w-[1240px] items-center justify-between gap-3 px-5 py-[18px] max-[400px]:gap-4 max-[400px]:px-4 max-[400px]:py-3 md:px-10 md:py-[26px]'
	);

	const logoClass = $derived(isCompact ? 'h-5 w-auto md:h-4' : 'h-5 w-auto md:h-[21px]');

	const navLinksClass = $derived(
		isCompact
			? 'hidden items-center gap-8 text-[0.83rem] font-medium tracking-[0.01em] md:flex md:gap-5 md:text-[0.75rem]'
			: 'hidden items-center gap-8 text-[0.83rem] font-medium tracking-[0.01em] md:flex'
	);

	const navLinkButtonClass = $derived(
		isCompact ? 'flex items-center gap-0.5 md:gap-1' : 'flex items-center gap-1'
	);

	const chevronClass = $derived(
		isCompact
			? 'size-3.5 transition-transform duration-200 md:size-3'
			: 'size-3.5 transition-transform duration-200'
	);

	const actionsClass = $derived(
		isCompact
			? 'flex min-w-0 shrink-0 items-center gap-2 max-[400px]:gap-1.5 md:gap-2'
			: 'flex min-w-0 shrink-0 items-center gap-2 max-[400px]:gap-1.5 md:gap-4'
	);

	const defaultCtaClass = $derived(
		isCompact
			? 'px-6 py-3.5 text-[0.86rem] md:px-3 md:py-1 md:text-[0.7rem] md:leading-tight'
			: 'px-6 py-3.5 text-[0.86rem]'
	);

	const loginLinkClass = $derived(
		isCompact
			? 'group hidden items-center gap-1 text-[0.78rem] font-semibold tracking-[0.01em] transition-colors md:inline-flex md:h-6 md:gap-0.5 md:text-[0.7rem] md:leading-none'
			: 'group hidden items-center gap-1.5 text-[0.78rem] font-semibold tracking-[0.01em] transition-colors md:inline-flex md:text-[0.83rem]'
	);

	const loginUnderlineClass = $derived(
		isLoginPage ? 'border-zp-ink' : 'border-transparent group-hover:border-zp-ink'
	);
</script>

<nav class={navShellClass}>
	<div class={innerShellClass}>
		<a href={resolve(localizeHref('/'))} class="shrink-0" aria-label={m.nav_aria_home()}>
			<enhanced:img
				src="/static/img/landing/redesign-2025/logo-navy.png?w=320"
				alt="Zarpar"
				sizes="152px"
				class={logoClass}
			></enhanced:img>
		</a>

		<div class={navLinksClass}>
			<a
				href={resolve(localizeHref('/barcos'))}
				class="text-zp-ink/78 transition-colors hover:text-zp-ink {canonicalPath === '/barcos'
					? 'text-zp-ink'
					: ''}"
			>
				{m.nav_boats()}
			</a>

			<div
				class="relative"
				role="presentation"
				onmouseenter={openMenu}
				onmouseleave={scheduleClose}
			>
				<button
					type="button"
					aria-haspopup="true"
					aria-expanded={open}
					onclick={() => (open = !open)}
					class="{navLinkButtonClass} text-zp-ink/78 transition-colors hover:text-zp-ink {howIsActive ||
					open
						? 'text-zp-ink'
						: ''}"
				>
					{m.landing_cta_how()}
					<ChevronDown class="{chevronClass} {open ? 'rotate-180' : ''}" />
				</button>

				{#if open}
					<div
						class="absolute top-full left-1/2 z-40 mt-3 grid w-[560px] -translate-x-1/2 grid-cols-[1fr_1fr] gap-2.5 overflow-hidden rounded-xl border border-zp-line bg-zp-paper p-2.5 shadow-[0_24px_50px_-24px_rgba(27,24,64,0.4)]"
						role="menu"
					>
						<a
							href={resolve(featured.href)}
							role="menuitem"
							onclick={() => (open = false)}
							class="nav-mod relative flex min-h-[240px] flex-col justify-end overflow-hidden rounded-lg p-4 text-zp-paper"
						>
							<span
								class="mb-2 text-[0.62rem] font-semibold tracking-[0.18em] text-zp-accent-light uppercase"
							>
								{featured.tag}
							</span>
							<span class="font-serif text-[1.25rem] leading-tight text-white"
								>{featured.label}</span
							>
							<span class="mt-1.5 text-[0.8rem] leading-snug text-zp-paper/82">{featured.desc}</span
							>
							<span
								class="nav-mod-lnk mt-3 inline-flex w-fit items-center gap-1.5 border-b border-zp-paper/35 pb-0.5 text-[0.78rem] font-semibold"
							>
								{featured.cta}
								<ArrowIcon />
							</span>
						</a>

						<div class="flex flex-col justify-center gap-0.5">
							{#each howItWorks as item (item.label)}
								<a
									href={resolve(item.href)}
									role="menuitem"
									onclick={() => (open = false)}
									class="block rounded-lg px-3.5 py-3 transition-colors hover:bg-zp-paper2 {item.match ===
									canonicalPath
										? 'bg-zp-paper2'
										: ''}"
								>
									<span class="block text-[0.92rem] font-semibold text-zp-ink">{item.label}</span>
									<span class="mt-0.5 block text-[0.8rem] leading-snug text-zp-muted"
										>{item.desc}</span
									>
								</a>
							{/each}
						</div>
					</div>
				{/if}
			</div>

			<a
				href={resolve(localizeHref('/registro-capitan'))}
				class="text-zp-ink/78 transition-colors hover:text-zp-ink {canonicalPath ===
				'/registro-capitan'
					? 'text-zp-ink'
					: ''}"
			>
				{m.nav_captain_short()}
			</a>
		</div>

		<div class={actionsClass}>
			<a
				href={ctaHref}
				class="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-zp-navy font-semibold tracking-[0.01em] whitespace-nowrap text-zp-paper transition-all hover:-translate-y-px hover:bg-zp-ink max-[400px]:px-3 max-[400px]:py-2 max-[400px]:text-[0.72rem] md:gap-2 {isCapitanPage
					? 'px-4 py-2.5 text-[0.78rem] max-[400px]:px-2.5 max-[400px]:py-1.5 max-[400px]:text-[0.68rem] md:px-6 md:py-3.5 md:text-[0.86rem]'
					: defaultCtaClass}"
			>
				{ctaLabel}
				{#if isAddBoatPage}
					<ArrowIcon />
				{/if}
			</a>
			<MarketingLocalePicker layout="inline" compact={isCompact} />
			{#if $currentUser}
				<div class="hidden md:flex md:items-center">
					<MarketingAccountMenu
						mode="dropdown"
						compact={isCompact}
						navVariant={appearance === 'overlay' ? 'overlay' : 'solid'}
					/>
				</div>
			{:else if showLoginLink}
				<a
					href={resolve(localizeHref('/iniciar-sesion'))}
					class="{loginLinkClass} {isLoginPage
						? 'text-zp-ink'
						: 'text-zp-ink/78 hover:text-zp-ink'}"
				>
					<span
						class="border-b pb-0.5 transition-[border-color] {loginUnderlineClass} {isCompact
							? 'md:pb-0'
							: ''}"
					>
						{m.new_upper_thrush_flow()}
					</span>
					<span
						class="shrink-0 transition-transform duration-200 group-hover:translate-x-1 {isCompact
							? '[&_svg]:size-3'
							: ''}"
					>
						<ArrowIcon />
					</span>
				</a>
			{/if}
		</div>
	</div>
</nav>

<style>
	.nav-mod {
		position: relative;
		isolation: isolate;
		transition: transform 0.3s ease;
	}

	.nav-mod::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -2;
		background-image: url('/img/landing/redesign-2025/photos/rafted-turquoise.avif');
		background-size: cover;
		background-position: center;
		transition: transform 0.5s ease;
	}

	.nav-mod::after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;
		background: linear-gradient(
			0deg,
			rgba(20, 17, 48, 0.86) 0%,
			rgba(20, 17, 48, 0.3) 55%,
			rgba(20, 17, 48, 0.1) 100%
		);
	}

	.nav-mod:hover {
		transform: translateY(-4px);
	}

	.nav-mod:hover::before {
		transform: scale(1.04);
	}

	.nav-mod:hover :global(.nav-mod-lnk svg) {
		transform: translateX(4px);
	}
</style>
